import { h, JSX, Ref, Fragment } from "preact";

import Link from "../../../foundation/router/Link";
import classname from "../../../foundation/utils/classname";
import { stopPropagation } from "../../../foundation/utils/legacy";

import IconComponentVariants from "../Icons/IconComponentVariants";
import IconGoToFilled from "../Icons/IconGoToFilled";
import IconLinkSmall from "../Icons/IconLinkSmall";
import Tooltip from "../Tooltip";

import styles from "./ComponentLabel.module.css";

const COMPONENT_TAG_MIN_WIDTH = 62;

interface Props extends JSX.HTMLAttributes<HTMLDivElement> {
    class?: string;
    variant: Variant;
    componentURL: string;
    componentName: string;
    minWidth?: number;
    maxWidth?: number;
    hasLayoutParent?: boolean;
    labelRef?: Ref<HTMLElement>;
    inspectableName: string;
    onVariantsButtonClick?: (ev: Event) => void;
    onGotoComponentClick?: (ev: Event) => void;
    onLinkButtonClick?: (ev: Event) => void;
}

enum Variant {
    Default = "default",
    Linked = "linked",
    LinkBroken = "linkBroken",
    Virtual = "virtual"
}

function ComponentLabel({
    class: cssClass,
    variant,
    componentName,
    componentURL,
    labelRef,
    hasLayoutParent: _nowUseless,
    minWidth,
    maxWidth,
    inspectableName,
    onVariantsButtonClick,
    onLinkButtonClick,
    onGotoComponentClick,
    ...divProps
}: Props) {
    function renderComponentTag() {
        if (variant === Variant.Virtual) {
            return (
                <div
                    class={styles.virtualComponentTag}
                    ref={labelRef as Ref<HTMLDivElement>}
                    title={componentName}>
                    <span class={classname(styles.componentLabelName, "caption-1", "medium", "ellipsis")}>
                        {componentName}
                    </span>
                </div>
            );
        }

        return (
            <Tooltip targetClass={styles.linkContainer} content="Go to Component Detail">
                <Link
                    class={styles.componentLink}
                    to={componentURL}
                    anchorRef={labelRef as Ref<HTMLAnchorElement>}
                    back={inspectableName}
                    onClick={onGotoComponentClick}>
                    {(!maxWidth || maxWidth > COMPONENT_TAG_MIN_WIDTH) && (
                        <span class={classname(styles.componentLabelName, "caption-1", "medium", "ellipsis")}>
                            {componentName}
                        </span>
                    )}
                    <IconGoToFilled class={styles.icon} color="var(--label-primary-inverse)"/>
                </Link>
            </Tooltip>
        );
    }

    return (
        <div
            class={classname(styles.componentLabel, cssClass, styles[variant])}
            style={{ minWidth, maxWidth }}
            onMouseDown={stopPropagation}
            {...divProps}>
            {renderComponentTag()}
            {onVariantsButtonClick && (
                <>
                    <div class={styles.seperator}/>
                    <Tooltip targetClass={styles.iconButtonContainer} content="Show all variants">
                        <button type="button" onClick={onVariantsButtonClick}>
                            <IconComponentVariants class={styles.icon} color="var(--label-primary-inverse)"/>
                        </button>
                    </Tooltip>
                </>
            )}
            {
                variant === Variant.Linked && (
                    <>
                        <div class={styles.seperator}/>
                        <Tooltip targetClass={styles.iconButtonContainer} content="Open connected component detail">
                            <button
                                type="button"
                                onClick={onLinkButtonClick}>
                                <IconLinkSmall class={styles.icon} color="var(--label-primary-inverse)"/>
                            </button>
                        </Tooltip>
                    </>
                )
            }
        </div>
    );
}

export default ComponentLabel;
export {
    Variant as ComponentLabelVariant
};
